/**
 * Hook de configuration et synchronisation de la langue pour les widgets
 *
 * Centralise la logique de détection, persistance et synchronisation
 * de la langue entre le widget (iframe) et le parent (loader.js).
 * Utilise en interne useWidgetTranslation pour le système de traduction.
 *
 * @module widgets/common/hooks/useWidgetLanguageSetup
 * @author Équipe Brydge
 * @since 2.0.0
 */

import {useEffect, useCallback} from 'react';
import {useWidgetTranslation} from 'packages/common/hooks/useWidgetTranslation';
import {detectLanguage, persistLanguage, resetLanguageToDefault} from '../utils/languageDetection';

/**
 * Hook de gestion complète de la langue pour un widget
 *
 * Prend en charge :
 * - Détection de la langue au montage (URL > localStorage > navigateur > défaut entreprise)
 * - Réinitialisation quand le multilingue est désactivé
 * - Synchronisation avec le parent via postMessage (bouton flottant)
 * - Handler de changement de langue avec persistance
 *
 * @param {Object} params - Paramètres du hook
 * @param {string} params.apiKey - Clé API pour l'authentification widget
 * @param {string} params.widgetType - Type de widget ('reservation' ou 'giftVoucher')
 * @param {Object} params.companyDetails - Détails de l'entreprise
 * @param {boolean} params.embedded - Si le widget est en mode iframe
 * @param {string} params.instanceId - Identifiant unique de l'instance widget
 * @param {URLSearchParams} params.searchParams - Parametres URL (de useSearchParams)
 * @param {Object} [params.widgetConfig] - Config widget contenant les traductions personnalisables
 * @return {Object} Utilitaires de traduction et gestion de langue
 */
export function useWidgetLanguageSetup({
  apiKey,
  widgetType,
  companyDetails,
  embedded,
  instanceId,
  searchParams,
  widgetConfig = null,
}) {
  const translationResult = useWidgetTranslation(apiKey, widgetType, companyDetails, widgetConfig);

  const {
    changeLanguage,
    currentLanguage,
    defaultLanguage,
    isMultiLanguageEnabled,
    enabledLanguages,
    isLoading: isTranslationsLoading,
  } = translationResult;

  // Detection et initialisation de la langue au montage
  useEffect(() => {
    if (companyDetails && isMultiLanguageEnabled && enabledLanguages) {
      const urlLang = searchParams.get('language');

      const detectedLang = detectLanguage(
          urlLang,
          defaultLanguage,
          enabledLanguages,
      );

      if (detectedLang !== currentLanguage) {
        changeLanguage(detectedLang);
      }
    }
  }, [companyDetails, isMultiLanguageEnabled, enabledLanguages, searchParams, defaultLanguage, currentLanguage, changeLanguage]);

  // Reinitialiser la langue quand le multilingue est desactive
  useEffect(() => {
    if (companyDetails && !isMultiLanguageEnabled && defaultLanguage) {
      if (currentLanguage !== defaultLanguage) {
        resetLanguageToDefault(defaultLanguage, changeLanguage);
      }
    }
  }, [companyDetails, isMultiLanguageEnabled, defaultLanguage, currentLanguage, changeLanguage]);

  // Synchroniser la langue avec le parent au montage pour le bouton flottant
  useEffect(() => {
    if (embedded && window.parent !== window && currentLanguage && !isTranslationsLoading) {
      window.parent.postMessage({
        type: 'brydge-language-init',
        language: currentLanguage,
        instanceId,
      }, '*');
    }
  }, [embedded, currentLanguage, instanceId, isTranslationsLoading]);

  // Handler de changement de langue avec persistance et notification parent
  const handleLanguageChange = useCallback((newLang) => {
    changeLanguage(newLang);
    persistLanguage(newLang);

    if (embedded && window.parent !== window) {
      window.parent.postMessage({
        type: 'brydge-language-change',
        language: newLang,
        instanceId,
      }, '*');
    }
  }, [changeLanguage, embedded, instanceId]);

  return {
    ...translationResult,
    handleLanguageChange,
  };
}
